import React from 'react';
import { ImageCounter, IMAGE_COUNTER_VARIANTS } from 'ui/image-counter';

/**
 * Gallery Slot Helpers
 * 
 * Reusable slot creators for gallery image counters.
 * Encapsulates the logic for when to show/hide counters.
 * 
 * **Design Pattern:**
 * - These helpers return slot functions (not components directly)
 * - The slot functions receive the actual image count at render time
 * - This respects the gallery test override (?gallery_test_count=N)
 * - Counter is hidden when total <= 1 image
 * 
 * **Usage:**
 * ```tsx
 * <Gallery
 *   counterSlot={createMobileCounterSlot(onImageClick)}
 *   seeAllSlot={createDesktopSeeAllSlot(onSeeAllClick)}
 * />
 * ```
 */

/**
 * Creates a mobile carousel counter slot
 * 
 * @param onImageClick - Callback when counter is clicked
 * @returns Counter slot function that respects image count
 */
export function createMobileCounterSlot(
  onImageClick?: (index: number) => void
): (current: number, total: number) => React.ReactNode {
  return (current, total) => {
    // Hide counter when only 1 image (respects gallery test override)
    if (total <= 1) return null;
    
    return (
      <ImageCounter
        current={current}
        total={total}
        variant={IMAGE_COUNTER_VARIANTS.MOBILE}
        onClick={() => onImageClick?.(0)}
      />
    );
  };
}

/**
 * Creates a desktop "See All" button slot
 * 
 * @param onSeeAllClick - Callback when "See All" is clicked
 * @returns See All slot function that respects image count
 */
export function createDesktopSeeAllSlot(
  onSeeAllClick?: () => void
): (total: number) => React.ReactNode {
  return total => {
    // Hide "See All" button when only 1 image (respects gallery test override)
    if (total <= 1) return null;
    
    return (
      <ImageCounter
        current={1}
        total={total}
        variant={IMAGE_COUNTER_VARIANTS.DESKTOP}
        onClick={onSeeAllClick}
      />
    );
  };
}
